import { useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Loader2, RefreshCw, Timer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePhoneSignStatus, type PortRequest } from '@/api/portage'
import { cn } from '@/lib/utils'

/** The server keeps a code 15 minutes. */
const LIFETIME_MS = 15 * 60 * 1000
/** "Signature received" stays on screen this long before the wizard moves on. */
const RECEIVED_PAUSE_MS = 2000

interface Props {
  companyId: number
  transferId: string
  codeId: number
  code: string
  /** When the code was made (local clock): the countdown does not depend on the server's. */
  createdAt: number
  locale: string
  renewing: boolean
  onRenew: () => void
  onCancel: () => void
  onSigned: (transfer: PortRequest) => void
}

function formatRemaining(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/**
 * Computer side of "Sign with my phone": the QR code, the link to type instead, a countdown,
 * and the wait for the phone. The server is asked every 3 seconds while the code is shown.
 */
export function PhoneSignPanel({
  companyId,
  transferId,
  codeId,
  code,
  createdAt,
  locale,
  renewing,
  onRenew,
  onCancel,
  onSigned,
}: Props) {
  const { t } = useTranslation()
  const [now, setNow] = useState(() => Date.now())
  const remaining = createdAt + LIFETIME_MS - now
  const timedOut = remaining <= 0
  const status = usePhoneSignStatus(companyId, transferId, codeId, !timedOut)
  const state = status.data?.state
  const received = state === 'signe'
  // Any other state than "waiting" means this code no longer works: a new one is needed.
  const dead = timedOut || (!!state && state !== 'valide' && state !== 'signe')

  useEffect(() => {
    if (received || dead) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [received, dead])

  // The parent's callback changes on every render: the pause must not start over each time.
  const signedRef = useRef(onSigned)
  useEffect(() => {
    signedRef.current = onSigned
  })
  const transfer = status.data?.transfer
  useEffect(() => {
    if (!received || !transfer) return
    const timer = window.setTimeout(() => signedRef.current(transfer), RECEIVED_PAUSE_MS)
    return () => window.clearTimeout(timer)
  }, [received, transfer])

  const url = `${window.location.origin}/${locale}/signer/${code}`
  const shortUrl = `${window.location.host}/${locale}/signer/${code}`

  if (received) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-6 py-10 text-center dark:border-emerald-900/50 dark:bg-emerald-900/20">
        <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
        <p className="text-lg font-semibold text-emerald-900 dark:text-emerald-200">{t('manager.numbers.port.qr.received')}</p>
        <p className="text-sm text-emerald-800/80 dark:text-emerald-300/80">{t('manager.numbers.port.qr.receivedHelp')}</p>
      </div>
    )
  }

  return (
    <div className="flex gap-6 rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="relative shrink-0 self-start rounded-xl bg-white p-3 ring-1 ring-slate-200 dark:ring-slate-700">
        <QRCodeSVG
          value={url}
          size={176}
          bgColor="#ffffff"
          fgColor="#0A192F"
          level="M"
          className={cn('block transition-opacity', dead && 'opacity-10')}
          aria-label={t('manager.numbers.port.qr.codeLabel')}
        />
        {dead && (
          <span className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm font-semibold text-slate-700">
            {timedOut || state === 'expire' ? t('manager.numbers.port.qr.expiredShort') : t('manager.numbers.port.qr.invalidShort')}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-3">
        <div>
          <p className="font-semibold text-slate-900 dark:text-white">{t('manager.numbers.port.qr.title')}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t('manager.numbers.port.qr.help')}</p>
        </div>

        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.port.qr.link')}</p>
          <p className="mt-0.5 select-all break-all font-mono text-sm text-slate-900 dark:text-slate-100">{shortUrl}</p>
        </div>

        {dead ? (
          <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
            {timedOut || state === 'expire' ? t('manager.numbers.port.qr.expired') : t('manager.numbers.port.qr.invalid')}
          </p>
        ) : (
          <div className="space-y-1.5">
            <p className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              {t('manager.numbers.port.qr.waiting')}
            </p>
            <p className="flex items-center gap-2 text-xs tabular-nums text-slate-500 dark:text-slate-400">
              <Timer className="h-3.5 w-3.5" />
              {t('manager.numbers.port.qr.validFor', { time: formatRemaining(remaining) })}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button
            type="button"
            variant={dead ? 'default' : 'outline'}
            size="sm"
            onClick={onRenew}
            disabled={renewing}
            className={cn(dead && 'bg-blue-500 hover:bg-blue-600 text-white')}
          >
            {renewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {t('manager.numbers.port.qr.newCode')}
          </Button>
          <button type="button" onClick={onCancel} className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
            {t('manager.numbers.port.qr.signHere')}
          </button>
        </div>
      </div>
    </div>
  )
}
